import React from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";

import { useIsDesktop } from "@hooks";

import { Footer } from "@pages/appLanding/components";
import { LanguagesCell } from "@pages/policy/components";

import { GitLabCICode, LangsFrameworksChart, SpeedChart } from "./components";

import GitLabLogoPng from "@assets/contributors/GitLabLogoNoText.png";
import MRComment from "@assets/gitlab-mr-comment.png";
import VulnReport from "@assets/gitlab-vulnerability-report.png";

const Container = styled.div`
  display: flex;
  justify-content: center;
  background-color: var(--global-light-gray);
`;

const DarkModeContainer = styled(Container)`
  color: var(--global-light-gray);
  background-color: var(--global-dark-gray);
  @media screen and (max-width: 900px) {
    flex-direction: column;
  }
`;

const ContentBox = styled.div`
  max-width: 1080px;
  margin: 40px 20px 20px 20px;
`;

const Header = styled.h2`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  margin-top: 40px;
  max-width: 936px;
  text-align: center;
  font-size: 60px;
  @media screen and (max-width: 900px) {
    font-size: 40px;
  }
  @media screen and (max-width: 415px) {
    font-size: var(--global-font-size-xl);
    margin-top: 100px;
  }
`;

const HeaderLogo = styled.img`
  height: 60px;
  margin-right: 20px;
  margin-left: -40px;
`;

const SubHeader = styled(Header)`
  margin-top: 0px;
  padding-bottom: 1em;
  font-size: var(--global-font-size-l);
  @media screen and (max-width: 415px) {
    font-size: var(--global-font-size-m);
  }
`;

const BodySubHeader = styled(SubHeader)`
  text-align: left;
  justify-content: left;
  color: var(--global-dark-gray);
`;

const Details = styled.div`
  font-size: var(--global-gont-size-s);
  margin: 20px;
  max-width: 400px;
`;

const Split = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const SplitRTL = styled(Split)`
  flex-wrap: wrap-reverse;
`;

const Body = styled.p`
  font-size: var(--global-font-size-s);
  color: var(--global-blue-gray);
`;

const Screenshot = styled.img`
  width: 40vw;
  max-width: 540px;
  @media screen and (max-width: 900px) {
    width: 80vw;
  }
  border-radius: 10px;
`;

const InlineLanguages = styled.span`
  margin-left: 8px;
  @media screen and (max-width: 1000px) {
    display: none;
  }
`;

export const GitLabSemgrepScreen: React.FC = () => {
  const defaultLangs = [
    "python",
    "typescript",
    "javascript",
    "docker",
    "ruby",
    "go",
    "java",
  ];

  const isDesktop = useIsDesktop();

  return (
    <div>
      <DarkModeContainer className="bp3-dark">
        <ContentBox style={{ maxWidth: "800px" }}>
          <Helmet>
            <title>Semgrep for GitLab</title>
            {/* <!-- Primary Meta Tags --> */}
            <meta name="title" content="Semgrep for GitLab" />
            <meta
              name="description"
              content="Get Semgrep scan results as MR comments, access to 1,000+ community-driven rules, and the power of easy-to-write custom rules using an intuitive syntax."
            />

            {/* <!-- Open Graph / Facebook --> */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://semgrep.dev/for/gitlab" />
            <meta property="og:title" content="Semgrep for GitLab" />
            <meta
              property="og:description"
              content="Get Semgrep scan results as MR comments, access to 1,000+ community-driven rules, and the power of easy-to-write custom rules using an intuitive syntax."
            />
            <meta
              property="og:image"
              content="/images/semgrep-for-gitlab-thumbnail-dark.png"
            />

            {/* <!-- Twitter Cards --> */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta
              property="twitter:url"
              content="https://semgrep.dev/for/gitlab"
            />
            <meta property="twitter:title" content="Semgrep for GitLab" />
            <meta
              property="twitter:description"
              content="Get Semgrep scan results as MR comments, access to 1,000+ community-driven rules, and the power of easy-to-write custom rules using an intuitive syntax."
            />
            <meta
              property="twitter:image"
              content="/images/semgrep-for-gitlab-thumbnail-dark.png"
            />
            <meta
              property="twitter:image:alt"
              content="GitLab hearts Semgrep"
            />
          </Helmet>
          <Header as="h1">
            <HeaderLogo src={GitLabLogoPng} alt="GitLab Logo" />
            GitLab + Semgrep
          </Header>
          <SubHeader as="p">
            Find bugs and enforce code standards
            <br /> on every merge request
          </SubHeader>
          <Split>
            <GitLabCICode withGitlabSastMention />
          </Split>
        </ContentBox>
      </DarkModeContainer>
      <Container>
        <ContentBox>
          <Split>
            <Details>
              <BodySubHeader>
                Scan merge requests with any of 1,000+&nbsp;rules
              </BodySubHeader>
              <Body>
                Customize your Semgrep scans using 1,000+ community rules.
                Semgrep supports 17&nbsp;languages, with rules for technologies
                like Docker, Kubernetes, secret scanning, and more.
              </Body>
              <Body>
                Go beyond existing registry rules by writing your own. Learn how
                in 5&nbsp;minutes.
              </Body>
            </Details>
            <Details>
              <LangsFrameworksChart
                columns={isDesktop ? 5 : 4}
                show_link={isDesktop}
              />
            </Details>
          </Split>
        </ContentBox>
      </Container>
      <DarkModeContainer>
        <ContentBox>
          <Split style={{ flexDirection: "row-reverse" }}>
            <Details>
              <BodySubHeader style={{ color: "var(--global-light-gray)" }}>
                Discuss findings in merge requests
              </BodySubHeader>
              <Body style={{ color: "var(--global-light-gray)" }}>
                Semgrep fits right into your existing developer workflow. Scans
                can report their results as merge request discussions, so they
                look just like regular code review.
              </Body>
              <i>Available with a free Semgrep App account.</i>
            </Details>
            <Details style={{ direction: "rtl" }}>
              <Screenshot
                src={MRComment}
                alt="An example GitLab Merge Request Comment"
              />
            </Details>
          </Split>
        </ContentBox>
      </DarkModeContainer>
      <Container>
        <ContentBox>
          <SplitRTL>
            <Details>
              <SpeedChart />
            </Details>
            <Details>
              <BodySubHeader>Get results at ludicrous speed</BodySubHeader>
              <Body>
                Semgrep CI skips unchanged files in merge requests and only
                reports newly introduced issues. Scans are 10x&nbsp;faster than
                other analyzers.
              </Body>
              <Body>
                Full scans of a branch still run at light speed: Semgrep
                finishes a deep scan of the Django project’s 100k&nbsp;lines of
                Python and JavaScript code in 1&nbsp;minute 4&nbsp;seconds.
              </Body>
            </Details>
          </SplitRTL>
        </ContentBox>
      </Container>
      <DarkModeContainer>
        <ContentBox>
          <Split>
            <Details>
              <BodySubHeader style={{ color: "var(--global-light-gray)" }}>
                Keep your GitLab SAST workflow
              </BodySubHeader>
              <Body style={{ color: "var(--global-light-gray)" }}>
                <p>
                  Semgrep can publish results to GitLab’s SAST ecosystem. Review
                  findings over time on the SAST dashboard, and see results in a
                  dedicated panel on the merge request page.
                </p>
                <i>Available in GitLab Ultimate.</i>
              </Body>
            </Details>
            <Details>
              <Screenshot
                src={VulnReport}
                alt="GitLab Vulnerability Report"
              ></Screenshot>
            </Details>
          </Split>
        </ContentBox>
      </DarkModeContainer>
      <Container>
        <ContentBox>
          <BodySubHeader>
            Add this in your .gitlab-ci.yml to scan{" "}
            <InlineLanguages>
              <LanguagesCell languages={defaultLangs} large />
            </InlineLanguages>
            &nbsp;for security issues
          </BodySubHeader>
          <GitLabCICode withSwitches withGitlabSastMention />
        </ContentBox>
      </Container>
      <Footer />
    </div>
  );
};
